import { serializeObjectToUrlParams } from '../utils/query';

type FilterValue = string | undefined;

export type PaginatedListParams<FilterKey extends string = string> = {
  page: number;
  perPage: number;
  search: string;
  sortBy?: string | undefined;
  sortDirection?: 'asc' | 'desc' | undefined;
  filters: Partial<Record<FilterKey, FilterValue>>;
};

export type PaginatedListConfig<FilterKey extends string = string> = {
  // Page size options
  readonly perPageOptions: readonly [number, ...number[]];
  // Key used for search query - used if the API uses something other than 'search' for search
  readonly searchKey: string;
  // Available filter keys
  readonly filterKeys: FilterKey[];
  // Prefix used to namespace URL state in case of multiple paginated lists on the same page
  readonly urlStatePrefix?: string;
};

// Interface for state returned by usePaginatedListState hooks.
export interface PaginatedListState<FilterKey extends string = string> {
  params: PaginatedListParams<FilterKey>;
  config: PaginatedListConfig<FilterKey>;
  setPage: (page: number) => void;
  setPerPage: (pageSize: number) => void;
  setSearch: (search: string) => void;
  setSort: (
    sortBy: PaginatedListParams['sortBy'],
    sortDirection: PaginatedListParams['sortDirection']
  ) => void;
  setFilter: (key: FilterKey, value: FilterValue) => void;
  clearFilters: () => void;
}

// Default configuration for paginated list state
export const DEFAULT_PAGINATED_LIST_CONFIG: PaginatedListConfig = {
  perPageOptions: [10, 20, 50],
  searchKey: 'search',
  urlStatePrefix: undefined,
  filterKeys: [],
};

// Get default params based on config
export const getDefaultPaginatedListParams = <FilterKey extends string>(
  config: PaginatedListConfig<FilterKey>
): PaginatedListParams<FilterKey> => ({
  page: 1,
  perPage: loadDefaultPerPage(config.perPageOptions),
  search: '',
  filters: {} as Partial<Record<FilterKey, FilterValue>>,
});

// Serialize params to URL query string
export const serializeParams = (
  params: PaginatedListParams,
  config: PaginatedListConfig,
  // Existing search string to merge with new params
  initialSearchString = ''
): string => {
  const queryParams = {
    page: params.page,
    per_page: params.perPage,
    sort: params.sortBy,
    direction: params.sortDirection,
    [config.searchKey || 'search']: params.search.trim() || undefined,
    ...params.filters,
  };

  return serializeObjectToUrlParams(
    queryParams,
    {
      keyPrefix: config.urlStatePrefix,
    },
    initialSearchString
  );
};

// Parse params from URL query string
export const parseParams = <FilterKey extends string>(
  urlSearchString: string,
  config: PaginatedListConfig<FilterKey>
): Partial<PaginatedListParams<FilterKey>> => {
  const searchParams = new URLSearchParams(urlSearchString);

  const getParam = (key: string) =>
    config.urlStatePrefix
      ? searchParams.get(`${config.urlStatePrefix}.${key}`)
      : searchParams.get(key);

  const parsedFilters = Object.fromEntries(
    config.filterKeys.map(key => [key, getParam(key)]).filter(([, value]) => value)
  );

  const hasAppliedFilters = !!Object.keys(parsedFilters).length;
  const parsePositiveInteger = (value: string | null) => {
    if (!value || !/^\d+$/.test(value)) {
      return undefined;
    }
    const parsedValue = Number(value);
    return parsedValue > 0 ? parsedValue : undefined;
  };

  const allParsedParams = {
    page: parsePositiveInteger(getParam('page')),
    perPage: parsePositiveInteger(getParam('per_page')),
    search: getParam(config.searchKey) || undefined,
    sortBy: getParam('sort') || undefined,
    sortDirection: (getParam('direction') ||
      undefined) as PaginatedListParams<FilterKey>['sortDirection'],
    filters: hasAppliedFilters ? parsedFilters : undefined,
  };

  // Remove undefined values
  const filteredParsedParams = Object.fromEntries(
    Object.entries(allParsedParams).filter(([, value]) => value !== undefined)
  );

  return filteredParsedParams;
};

// Convert PaginatedListState to a typed params object for generated API hooks
export const paginatedParams = <FilterKey extends string>(
  state: PaginatedListState<FilterKey>
) => ({
  page: state.params.page,
  per_page: state.params.perPage,
  ...(state.params.search && { [state.config.searchKey ?? 'search']: state.params.search }),
  ...(state.params.sortBy && {
    sort: state.params.sortBy,
    direction: state.params.sortDirection,
  }),
  ...state.params.filters,
});

// Serialize params for use in backend API query
export const serializeParamsForQuery = <FilterKey extends string>(
  state: PaginatedListState<FilterKey>
): string => {
  // When serializing params for API query, we don't want to include the URL prefix
  const configWithDisabledURLPrefix = { ...state.config, urlStatePrefix: undefined };

  return serializeParams(state.params, configWithDisabledURLPrefix);
};

// Create a static paginated list state with fixed parameters
export const createStaticPaginatedListState = (
  config: Partial<Omit<PaginatedListConfig, 'urlStatePrefix' | 'perPageOptions'>> = {},
  params: Partial<PaginatedListParams> = {}
): PaginatedListState => {
  const DEFAULT_STATIC_PER_PAGE = 100;
  const perPage = params.perPage || DEFAULT_STATIC_PER_PAGE;

  return {
    config: {
      searchKey: config.searchKey || 'search',
      filterKeys: config.filterKeys || [],
      perPageOptions: [perPage],
    },
    params: {
      page: params.page || 1,
      perPage,
      search: params.search || '',
      filters: params.filters || {},
    },
    setPage: () => {},
    setPerPage: () => {},
    setSearch: () => {},
    setSort: () => {},
    setFilter: () => {},
    clearFilters: () => {},
  };
};

const getPerPageKey = (perPageOptions: readonly number[]) =>
  `rocket:tablePerPage:${perPageOptions}`;

export function loadDefaultPerPage(perPageOptions: readonly number[]) {
  try {
    const savedValue = Number(window.localStorage.getItem(getPerPageKey(perPageOptions)));
    if (perPageOptions.includes(savedValue)) {
      return savedValue;
    }
  } catch {
    // Fall back to the first configured option when storage is unavailable.
  }
  return perPageOptions[0] || 10;
}

export function saveDefaultPerPage(perPageOptions: readonly number[], value: number) {
  try {
    window.localStorage.setItem(getPerPageKey(perPageOptions), String(value));
  } catch {
    // error
  }
}
